import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type { Maintenance, MaintenanceStatus } from '@shared/definitions/maintenance';
import type { MaintenanceLog } from '@shared/definitions/maintenance-log';



/**
 * Pattern B contract for Maintenance.
 *
 * Workspace-scoped. Supports filtering by status / property.
 */
export interface IMaintenanceStore {
  readonly maintenance: Signal<readonly Maintenance[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;
  readonly activeCount: Signal<number>;
  readonly completedCount: Signal<number>;
  readonly counts: Signal<{
    scheduled: number;
    inProgress: number;
    completed: number;
    overdue: number;
    canceled: number;
    pending: number;
    total: number;
  }>;

  /**
   * (Re)load the full workspace list. Idempotent by default: a no-op when the
   * live listener is already open on the same (user, workspace) scope. Pass
   * `force = true` for an explicit manual refresh (e.g. the interventions hub).
   */
  refresh(force?: boolean): Promise<void>;

  getMaintenanceById(maintenanceUID: string): Maintenance | null;
  getMaintenanceByStatus(status: MaintenanceStatus): readonly Maintenance[];
  getMaintenanceForProperty(propertyUID: string): readonly Maintenance[];
  getMaintenanceForBuilding(buildingUID: string): readonly Maintenance[];
  getActiveMaintenance(): readonly Maintenance[];

  addMaintenance(maintenance: Maintenance): Promise<string>;
  updateMaintenance(maintenance: Maintenance): Promise<void>;
  updateMaintenanceStatus(maintenanceUID: string, status: MaintenanceStatus, note?: string): Promise<void>;
  deleteMaintenance(maintenanceUID: string): Promise<void>;

  /** Status change + log entry in one call — writes a completion/transition log. */
  updateMaintenanceStatusWithLog(
    maintenanceUID: string,
    status: MaintenanceStatus,
    notes?: string,
    documentUIDs?: string[]
  ): Promise<void>;

  /** Append a free-form log/audit entry to a maintenance task. */
  addGeneralLog(
    maintenanceUID: string,
    action: string,
    notes?: string,
    documentUIDs?: string[]
  ): Promise<string>;

  /** Completion-history subcollection for a maintenance task (ordered desc by completionDate). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCompletionHistory(maintenanceUID: string): Promise<any[]>;

  /** All log entries for a maintenance task. */
  getMaintenanceLogs(maintenanceUID: string): Promise<MaintenanceLog[]>;
}

export type MaintenanceStoreMode = 'mock' | 'firestore';

export function resolveMaintenanceStoreMode(): MaintenanceStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('maintenance.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('maintenance.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}

export const MAINTENANCE_STORE = new InjectionToken<IMaintenanceStore>('MAINTENANCE_STORE');
